import { MessageSquare, Search } from "lucide-react";
import React from "react";

import { Input } from "@/components/ui/input";
import { useI18n } from "@/context/i18n";
import { cn } from "@/lib/utils";

import type { TWhatsappConversationThread } from "../partner-whatsapp-inbox.types";
import {
  formatPhoneDisplay,
  formatThreadTime,
} from "../partner-whatsapp-inbox.utils";
import { InboxEmptyState } from "./inbox-empty-state";
import { ThreadAvatar } from "./thread-avatar";
import { ThreadListSkeleton } from "./thread-list-skeleton";

export interface ThreadListProps {
  threads: TWhatsappConversationThread[];
  selectedThreadId: string | null;
  search: string;
  onSearchChange: (value: string) => void;
  onSelectThread: (threadId: string) => void;
  isLoading?: boolean;
}

export const ThreadList: React.FC<ThreadListProps> = ({
  threads,
  selectedThreadId,
  search,
  onSearchChange,
  onSelectThread,
  isLoading,
}) => {
  const { t, locale: ctxLocale } = useI18n();
  const dateLocale = ctxLocale === "en-US" ? "en-US" : "pt-BR";

  return (
    <div className="flex flex-col h-full min-h-0 bg-card">
      <div className="shrink-0 px-3 py-3 border-b border-border/80 space-y-2">
        <div className="flex items-center justify-between gap-2 px-0.5">
          <h2 className="text-sm font-semibold">
            {t("partnerWhatsappInbox.threads.title")}
          </h2>
          {!isLoading && threads.length > 0 ? (
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {threads.length}
            </span>
          ) : null}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-9 h-9 bg-muted/40 border-transparent focus-visible:border-border"
            placeholder={t("partnerWhatsappInbox.threads.search")}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? <ThreadListSkeleton /> : null}

        {!isLoading && threads.length === 0 ? (
          <InboxEmptyState
            compact
            icon={MessageSquare}
            title={t("partnerWhatsappInbox.threads.emptyTitle")}
            description={t("partnerWhatsappInbox.threads.empty")}
          />
        ) : null}

        {!isLoading && threads.length > 0 ? (
          <ul className="divide-y divide-border/50">
            {threads.map((thread) => {
              const active = thread.id === selectedThreadId;
              const title =
                thread.display_name?.trim() ||
                formatPhoneDisplay(thread.phone_e164);
              const timeLabel = formatThreadTime(
                thread.last_message_at,
                dateLocale,
              );

              return (
                <li key={thread.id}>
                  <button
                    type="button"
                    onClick={() => onSelectThread(thread.id)}
                    className={cn(
                      "w-full text-left flex gap-3 px-3 py-3 transition-colors",
                      "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                      active &&
                        "bg-emerald-500/10 border-l-[3px] border-l-emerald-500 pl-[9px]",
                    )}
                  >
                    <ThreadAvatar
                      displayName={thread.display_name}
                      phone={thread.phone_e164}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="font-medium text-sm truncate">{title}</p>
                        {timeLabel ? (
                          <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
                            {timeLabel}
                          </span>
                        ) : null}
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {formatPhoneDisplay(thread.phone_e164)}
                      </p>
                      {thread.last_message_preview ? (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                          {thread.last_message_preview}
                        </p>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
};
